import { useEffect, useState, useCallback } from "react";
import { Plus, UserCog, ShieldCheck, Pencil, Power } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Loader } from "@/components/common";
import Modal, { TextField, SelectField } from "@/components/Modal";
import { PageTitle, Panel, STAFF_MODULES } from "./AdminShell";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Super Admin", admin: "Administrateur", commercial: "Commercial", comptable: "Comptable",
  responsable_logistique: "Responsable logistique", responsable_installation: "Responsable installation",
};
const CREATABLE = ["commercial", "comptable", "responsable_logistique", "responsable_installation", "admin"];

export default function AdminStaff() {
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState(null); // {mode:'create'|'edit', user}
  const load = useCallback(() => api.get("/admin/staff").then((r) => setStaff(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!staff) return <Loader />;

  const toggle = async (s) => {
    try { await api.put(`/admin/staff/${s.user_id}`, { active: !s.active }); toast.success(s.active ? "Compte désactivé" : "Compte activé"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageTitle action={<button onClick={() => setForm({ mode: "create" })} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-1" data-testid="add-staff-btn"><Plus className="w-4 h-4" /> Créer un utilisateur</button>}>Utilisateurs</PageTitle>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {staff.map((s) => (
          <Panel key={s.user_id} className="p-4" data-testid={`staff-card-${s.user_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-lg bg-[#0F172A] flex items-center justify-center">
                {s.role === "super_admin" ? <ShieldCheck className="w-5 h-5 text-[#E08B00]" /> : <UserCog className="w-5 h-5 text-white" />}
              </div>
              <div><p className="font-semibold">{s.name}</p><p className="text-xs text-[#C85A32] font-semibold">{ROLE_LABELS[s.role] || s.role}</p></div>
              <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${s.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`} data-testid={`staff-status-${s.user_id}`}>{s.active ? "Actif" : "Inactif"}</span>
            </div>
            <p className="text-xs text-slate-500">{s.email} {s.phone && `· ${s.phone}`}</p>
            {["admin", "super_admin"].includes(s.role)
              ? <p className="text-xs text-slate-400 mt-1">Accès complet à tous les modules</p>
              : <p className="text-xs text-slate-400 mt-1">{(s.modules || []).length ? `${s.modules.length} module(s)` : "Accès par défaut du rôle"}</p>}
            {s.role !== "super_admin" && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => setForm({ mode: "edit", user: s })} className="flex-1 border border-slate-200 text-slate-700 text-xs font-bold py-1.5 rounded-lg inline-flex items-center justify-center gap-1" data-testid={`staff-edit-${s.user_id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</button>
                <button onClick={() => toggle(s)} className={`flex-1 text-xs font-bold py-1.5 rounded-lg inline-flex items-center justify-center gap-1 ${s.active ? "border border-red-200 text-red-600" : "border border-green-200 text-green-600"}`} data-testid={`staff-toggle-${s.user_id}`}><Power className="w-3.5 h-3.5" /> {s.active ? "Désactiver" : "Activer"}</button>
              </div>
            )}
          </Panel>
        ))}
      </div>

      {form && <StaffForm form={form} onClose={() => setForm(null)} onDone={load} />}
    </div>
  );
}

function StaffForm({ form, onClose, onDone }) {
  const isEdit = form.mode === "edit";
  const u = form.user || {};
  const initPerms = () => {
    const p = { ...(u.permissions || {}) };
    if (!Object.keys(p).length && Array.isArray(u.modules)) u.modules.forEach((m) => { p[m] = { view: true, edit: false, delete: false }; });
    return p;
  };
  const [f, setF] = useState({ name: u.name || "", email: u.email || "", phone: u.phone || "", password: "", role: u.role || "commercial", permissions: initPerms() });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const setPerm = (mod, act) => setF((s) => {
    const cur = s.permissions[mod] || { view: false, edit: false, delete: false };
    const nv = { ...cur, [act]: !cur[act] };
    if (act === "view" && !nv.view) { nv.edit = false; nv.delete = false; }
    if ((act === "edit" || act === "delete") && nv[act]) nv.view = true;
    return { ...s, permissions: { ...s.permissions, [mod]: nv } };
  });
  const fullAccess = ["admin", "super_admin"].includes(f.role);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        const payload = { name: f.name, phone: f.phone, role: f.role, permissions: f.permissions };
        if (f.password) payload.password = f.password;
        await api.put(`/admin/staff/${u.user_id}`, payload);
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/admin/staff", f);
        toast.success("Utilisateur créé");
      }
      onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title={isEdit ? `Modifier — ${u.name}` : "Créer un utilisateur"} onClose={onClose} testid="staff-form" size="lg">
      <form onSubmit={submit} className="space-y-3">
        <SelectField label="Rôle" value={f.role} onChange={set("role")} testid="staff-role" placeholder={null}
          options={CREATABLE.map((r) => ({ value: r, label: ROLE_LABELS[r] }))} />
        <TextField label="Nom complet" value={f.name} onChange={set("name")} required testid="staff-name" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Email" type="email" value={f.email} onChange={set("email")} required={!isEdit} testid="staff-email" placeholder={isEdit ? "(non modifiable)" : ""} />
          <TextField label="Téléphone" value={f.phone} onChange={set("phone")} testid="staff-phone" required={false} />
        </div>
        <TextField label={isEdit ? "Nouveau mot de passe (facultatif)" : "Mot de passe"} type="password" value={f.password} onChange={set("password")} required={!isEdit} testid="staff-password" />

        <div>
          <span className="text-sm font-semibold text-slate-700 block mb-1.5">Permissions par module</span>
          {fullAccess ? <p className="text-xs text-slate-400">Ce rôle a un accès complet à tous les modules.</p> : (
            <div className="space-y-1.5" data-testid="staff-modules">
              <div className="grid grid-cols-[1fr_2.6rem_2.6rem_2.6rem] gap-2 text-[10px] font-bold text-slate-400 px-2 text-center">
                <span className="text-left">Module</span><span>Voir</span><span>Créer / Mod.</span><span>Suppr.</span>
              </div>
              {STAFF_MODULES.map(([key, label]) => {
                const p = f.permissions[key] || {};
                return (
                  <div key={key} className={`grid grid-cols-[1fr_2.6rem_2.6rem_2.6rem] gap-2 items-center text-xs px-2 py-1.5 rounded-lg border ${p.view ? "border-[#C85A32] bg-[#FDF3EE]" : "border-slate-200"}`}>
                    <span>{label}</span>
                    <input type="checkbox" className="mx-auto" checked={!!p.view} onChange={() => setPerm(key, "view")} data-testid={`perm-${key}-view`} />
                    <input type="checkbox" className="mx-auto" checked={!!p.edit} onChange={() => setPerm(key, "edit")} data-testid={`perm-${key}-edit`} />
                    <input type="checkbox" className="mx-auto" checked={!!p.delete} onChange={() => setPerm(key, "delete")} data-testid={`perm-${key}-delete`} />
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-1">« Créer / Mod. » couvre la création et la modification. Cocher une action active automatiquement « Voir ».</p>
        </div>

        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="staff-submit">{isEdit ? "Enregistrer" : "Créer le compte"}</button>
      </form>
    </Modal>
  );
}
